<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, type ComponentPublicInstance } from "vue";
import { CircleCheck, MessageSquareHeart, Star, X } from "@lucide/vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import TextFieldInput from "@/components/Reusables/Inputs/TextFieldInput.vue";
import { WelcomeConfetti } from "@/features/WelcomeFlow";
import { useFlowGuideCoordinationStore } from "@/stores/flowGuide/flowGuideCoordination.store";
import { usePlatformReviewStore } from "./platformReview.store";
import { submit, dismiss } from "./usePlatformReview.composable";

// Belt-and-braces, alongside the composable's own gate: if a welcome tour somehow starts while this
// card is already open, hide it rather than let the two overlap on screen.
const coordinationStore = useFlowGuideCoordinationStore();
const isWelcomeTourActive = computed(() => coordinationStore.isWelcomeTourActive);
const reviewStore = usePlatformReviewStore();

const HEADING_ID = "platform-review-heading";
const CAPTION_ID = "platform-review-caption";
const STAR_COUNT = 5;
const stars = [0, 1, 2, 3, 4];
const SCORE_LABELS = ["Not great", "Could be better", "Okay", "Good", "Love it"];
// Long enough to read two short lines; the confetti has settled by then.
const THANKS_VISIBLE_MS = 3200;

const score = ref(0);
const hoveredIndex = ref<number | null>(null);
const comment = ref("");
const isSending = ref(false);
const sent = ref(false);
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const displayScore = computed(() => (hoveredIndex.value ?? score.value - 1) + 1);
const scoreLabel = computed(() => (displayScore.value > 0 ? SCORE_LABELS[displayScore.value - 1] : "Pick a star"));
const hasScore = computed(() => score.value > 0);
const canSend = computed(() => hasScore.value && !isSending.value);
// Announced only when a score is committed: the visible label follows the pointer, and a live region
// bound to it would read out every star the mouse crosses.
const committedLabel = computed(() => (hasScore.value ? SCORE_LABELS[score.value - 1] : ""));
const thanksLine = computed(() => {
  if (score.value >= 4) return "Glad it's working for you.";
  if (score.value === 3) return "Thanks for the honest read.";
  return "Sorry it isn't there yet.";
});
// Roving tabindex: only the chosen star (or the first, before any choice) sits in the tab order —
// arrow keys move focus and the score together, matching SwitchTabs' keyboard pattern.
const rovingIndex = computed(() => (score.value > 0 ? score.value - 1 : 0));

const starRefs = ref<(HTMLButtonElement | null)[]>([]);
// Native <button>s only ever hand back an Element here — the ComponentPublicInstance branch is
// template-ref typing boilerplate for elements that could be components.
const setStarRef = (element: Element | ComponentPublicInstance | null, index: number) => {
  starRefs.value[index] = element as HTMLButtonElement | null;
};

const setScore = (index: number) => {
  score.value = index + 1;
};

const onStarKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key === "ArrowRight") {
    event.preventDefault();
    const next = Math.min(index + 1, STAR_COUNT - 1);
    setScore(next);
    starRefs.value[next]?.focus();
  } else if (event.key === "ArrowLeft") {
    event.preventDefault();
    const previous = Math.max(index - 1, 0);
    setScore(previous);
    starRefs.value[previous]?.focus();
  }
};

const handleSend = async () => {
  if (!canSend.value) return;
  isSending.value = true;
  try {
    await submit(score.value, comment.value);
  } finally {
    isSending.value = false;
  }
  sent.value = true;
  closeTimer = setTimeout(() => reviewStore.close(), THANKS_VISIBLE_MS);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Escape" || isWelcomeTourActive.value) return;
  // Once sent there is nothing left to abandon — Escape just closes early.
  if (sent.value) {
    reviewStore.close();
    return;
  }
  // Escape inside the comment box must not throw away what was typed — and "Not now" is final.
  if (event.target instanceof HTMLTextAreaElement) return;
  dismiss();
};

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  if (closeTimer !== null) clearTimeout(closeTimer);
});

// Same token-styled native buttons as WelcomeTourCard: this card belongs to the tour family.
const actionClass =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <Transition name="fade-bottom">
    <div
      v-if="!isWelcomeTourActive"
      role="dialog"
      :aria-labelledby="HEADING_ID"
      :aria-describedby="CAPTION_ID"
      class="z-overlay fixed right-6 bottom-6 w-[380px] max-w-[calc(100vw-2rem)]">
      <!-- WelcomeTourCard's shell (shadow-dropdown + slate-200 ring) rather than the Card reusable:
           the card floats above the page like a tour step, and the header band needs to bleed to
           the corners. overflow-hidden is safe here — nothing inside opens a dropdown — and it keeps
           the thank-you confetti inside the card. -->
      <div class="bg-slate-0 shadow-dropdown rounded-16 relative flex flex-col overflow-hidden ring-1 ring-slate-200">
        <!-- Positioned before the content so the (also positioned) text paints above the pieces. -->
        <WelcomeConfetti
          v-if="sent"
          :duration-ms="2600"
          :piece-count="90" />
        <Transition
          name="fade-in"
          mode="out-in">
          <div
            v-if="sent"
            key="thanks"
            role="status"
            class="relative flex flex-col items-center gap-2 px-5 py-8 text-center">
            <span class="bg-app-green-50 mb-1 rounded-full p-3">
              <CircleCheck
                class="icon-xl text-app-green-500"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <h2
              :id="HEADING_ID"
              class="t-sh3 text-type-default">
              Thank you!
            </h2>
            <p
              :id="CAPTION_ID"
              class="t-small text-type-muted max-w-[280px]">
              {{ thanksLine }} Your answer goes straight to the team building Real-Time LCA.
            </p>
          </div>

          <div
            v-else
            key="form"
            class="flex flex-col">
            <!-- The blue→purple band is this card's own signature: a one-off nudge, not a settings card,
                 so it may blend the two brand families where the Step Cards keep one flat tint. The tile
                 stays flat — the shell already casts the only shadow. -->
            <div class="from-app-blue-50 to-app-purple-50 relative flex items-start gap-3 bg-linear-to-br px-5 pt-4 pb-3">
              <span class="bg-slate-0 rounded-8 flex shrink-0 items-center justify-center p-2">
                <MessageSquareHeart
                  class="icon-md"
                  :stroke-width="1.5"
                  color="var(--color-app-blue-800)"
                  fill="var(--color-app-blue-100)"
                  aria-hidden="true" />
              </span>
              <div class="flex flex-col gap-1 pr-4">
                <h2
                  :id="HEADING_ID"
                  class="t-sh4 text-type-default">
                  How is the new Real-Time LCA working for you?
                </h2>
                <p
                  :id="CAPTION_ID"
                  class="t-small text-type-muted">
                  Pick a rating, add a line if you like, then send.
                </p>
              </div>
              <!-- Dismiss is a quiet cross in the corner, not a footer button: on an untouched card the
                   stars should be the only action the eye lands on. -->
              <button
                type="button"
                class="focus-ring interactive-active duration-fast ease-standard text-type-muted hover:text-type-default absolute top-3 right-3 rounded-full p-1 transition-colors hover:bg-slate-900/10"
                aria-label="Not now"
                @click="dismiss">
                <X
                  class="icon-sm"
                  :stroke-width="1.5"
                  aria-hidden="true" />
              </button>
            </div>

            <div class="flex flex-col gap-3 px-5 pt-4 pb-4">
              <!-- Rating is centred and Send runs the full width: the card's rows share edges rather than
                   a left-aligned star row facing a right-aligned button. -->
              <div class="flex flex-col items-center gap-1">
                <div
                  class="flex gap-2"
                  role="radiogroup"
                  aria-label="Rating">
                  <button
                    v-for="index in stars"
                    :key="index"
                    :ref="(element) => setStarRef(element, index)"
                    type="button"
                    role="radio"
                    class="focus-ring rounded-8 duration-fast ease-standard p-1 transition-transform hover:scale-110"
                    :tabindex="index === rovingIndex ? 0 : -1"
                    :aria-label="`Rate ${index + 1} of 5`"
                    :aria-checked="score === index + 1"
                    @mouseenter="hoveredIndex = index"
                    @mouseleave="hoveredIndex = null"
                    @click="setScore(index)"
                    @keydown="onStarKeydown($event, index)">
                    <Star
                      class="icon-lg duration-fast ease-standard transition-colors"
                      :stroke-width="1.5"
                      :class="index < displayScore ? 'text-app-yellow-500' : 'text-slate-300'"
                      :fill="index < displayScore ? 'var(--color-app-yellow-400)' : 'none'" />
                  </button>
                </div>
                <span
                  class="t-small-bold"
                  :class="displayScore > 0 ? 'text-type-default' : 'text-type-muted'"
                  aria-hidden="true">
                  {{ scoreLabel }}
                </span>
                <span
                  class="sr-only"
                  role="status">
                  {{ committedLabel }}
                </span>
              </div>

              <TextFieldInput
                :value="comment"
                color="primary"
                size="h-16 w-full"
                placeholder="What works, what doesn't? (optional)"
                @update:value="comment = $event" />

              <button
                type="button"
                :class="[
                  actionClass,
                  'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover relative w-full text-white',
                  { 'interactive-disabled': !canSend },
                ]"
                :disabled="!canSend"
                @click="handleSend">
                <span :class="{ 'opacity-0': isSending }">Send</span>
                <LoadingSpinner
                  v-if="isSending"
                  class="absolute-center"
                  size="xs"
                  fill="primary" />
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>
